import type { ActionResult, PennylaneInvoiceListItem } from '@flux/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { forceSendInvoice, ignoreInvoice, refreshInvoice, resendInvoice } from './api.js';

/**
 * Mutations partagées « Mettre à jour depuis Pennylane », « Renvoyer à
 * Martelet », « Forcer l'envoi », « Ignorer » d'une
 * facture Pennylane (`02` §3 : actions de ligne dans Commandes, et boutons de
 * la fiche facture). Une facture déjà envoyée déclenche un `ConfirmDialog`
 * côté écran avant l'appel (contrat `01` §6.4 : `confirmResend`) : pas de
 * nouvel essai après un 409 ici, l'écran connaît déjà le statut de la ligne.
 */
function useInvoiceMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<PennylaneInvoiceListItem>,
  successMessage: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      toast.success(successMessage);
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
}

/** Le toast reprend le verdict du serveur : ce que donnerait un renvoi maintenant. */
export function useRefreshInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<ActionResult> => refreshInvoice(id),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Facture mise à jour depuis Pennylane.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
}

export function useResendInvoice() {
  return useInvoiceMutation(
    ({ id, confirmResend }: { id: string; confirmResend: boolean }) => resendInvoice(id, confirmResend),
    'Renvoi à Martelet demandé.',
  );
}

export function useForceSendInvoice() {
  return useInvoiceMutation((id: string) => forceSendInvoice(id), 'Envoi forcé.');
}

export function useIgnoreInvoice() {
  return useInvoiceMutation(
    ({ id, reason }: { id: string; reason: string }) => ignoreInvoice(id, reason),
    'Facture ignorée.',
  );
}
